import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface FilterMenuItem {
  id: string;
  label: string;
}

interface FilterMenuProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onSelect: (itemId: string) => void;
  headerText: string;
  menuItems: FilterMenuItem[];
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  headerText,
  menuItems,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedValue, setSelectedValue] = useState<string>("");

  const handleItemSelect = (itemId: string) => {
    onSelect(itemId);
    onClose();
  };

  const handleMobileSelect = (value: string) => {
    setSelectedValue(value);
  };

  const handleMobileApply = () => {
    if (selectedValue) {
      onSelect(selectedValue);
    }
    onClose();
  };

  const handleMobileCancel = () => {
    setSelectedValue("");
    onClose();
  };

  // Mobile full-screen modal
  if (isSmallScreen) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        disableAutoFocus={false}
        disableEnforceFocus={false}
        disableRestoreFocus={false}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "stretch",
            justifyContent: "stretch",
          },
          "& .MuiDialog-paper": {
            margin: 0,
            maxWidth: "none",
            maxHeight: "none",
            position: "fixed",
            top: 64, // Position below navbar
            left: 0,
            right: 0,
            bottom: 0,
            height: "calc(100vh - 64px)", // Take remaining height below navbar
            width: "100vw",
            borderRadius: 0,
            transform: "none !important", // Prevent any transform animations
          },
          "& .MuiBackdrop-root": {
            top: 64, // Backdrop also starts below navbar
            height: "calc(100vh - 64px)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Typography
            component="div"
            variant="h6"
            sx={{
              fontWeight: 600,
              textAlign: "left",
              flex: 1,
            }}
          >
            {headerText}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            px: 3,
            pt: 4, // Added top padding
            pb: 3,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 1, // Reduced from mb: 2 to mb: 1 for smaller gap
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            {headerText === "Sort by" ? "Sort documents by" : "Choose language"}
          </Typography>

          <FormControl fullWidth>
            <Select
              value={selectedValue}
              onChange={(e) => handleMobileSelect(e.target.value)}
              displayEmpty
              autoFocus
              sx={{
                "& .MuiSelect-select": {
                  py: 2,
                  display: "flex",
                  alignItems: "center", // Vertical alignment
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography
                  color="textSecondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {headerText === "Sort by"
                    ? "Select sort option..."
                    : "Select language..."}
                </Typography>
              </MenuItem>
              {menuItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        {/* Footer Buttons */}
        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
            px: 3,
            py: 2,
            gap: 2,
            flexDirection: "column",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleMobileCancel}
            fullWidth
            sx={{ mb: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMobileApply}
            disabled={!selectedValue}
            fullWidth
          >
            {headerText === "Sort by" ? "Sort" : "Choose"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop dropdown menu (original behavior)
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 200, // Constant width for menu
          },
        },
      }}
    >
      {/* Menu Header */}
      <MenuItem
        disabled
        sx={{
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          pl: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {headerText}
        </Typography>
      </MenuItem>

      {/* Menu Items */}
      {menuItems.map((item) => (
        <MenuItem
          key={item.id}
          onClick={() => handleItemSelect(item.id)}
          sx={{ pl: 2 }}
        >
          <ListItemText>{item.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );
};

export default FilterMenu;
